import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { bulkAssignClients, createClient, listClients } from "../api/endpoints/clients"
import { generateReport } from "../api/endpoints/reports"
import { listUsers } from "../api/endpoints/users"
import { ClientDetailDrawer } from "../components/client_detail_drawer"
import {
  Button,
  BusinessPageHeader,
  ContentCard,
  DataTable,
  EmptyState,
  FilterField,
  FilterToolbar,
  FormField,
  Input,
  MetricGrid,
  Modal,
  Select,
  StatCard,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  TableSkeleton,
  Td,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatEnumLabel } from "../utils/formatting"

interface ClientFormState {
  fullName: string
  phoneNumber: string
  region: string
}

const DEFAULT_FORM: ClientFormState = {
  fullName: "",
  phoneNumber: "",
  region: "",
}

function kycTone(status?: string): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "APPROVED":
      return "success"
    case "REJECTED":
      return "danger"
    case "PENDING":
      return "warning"
    default:
      return "neutral"
  }
}

function overdueCategoryTone(
  category?: string | null,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (category) {
    case "S1":
      return "info"
    case "CATEGORY_A":
    case "CATEGORY_B":
      return "warning"
    case "CATEGORY_C":
      return "danger"
    default:
      return "neutral"
  }
}

export function ClientListPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "")
  const [selectedClientId, setSelectedClientId] = useState<string | null>(
    () => searchParams.get("selected_client_id") ?? null,
  )
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([])
  const [bulkAgentId, setBulkAgentId] = useState("")
  const [form, setForm] = useState<ClientFormState>(DEFAULT_FORM)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const [kycFilter, setKycFilter] = useState("")
  const [overdueCategoryFilter, setOverdueCategoryFilter] = useState("")
  const [regionFilter, setRegionFilter] = useState("")
  const [pipelineFilter, setPipelineFilter] = useState("")
  const [assignedFilter, setAssignedFilter] = useState("")
  const [loanTypeFilter, setLoanTypeFilter] = useState("")

  const canCreateClients = user?.permissions?.canEditClients ?? user?.role === "SUPER_ADMIN"
  const canAssignClients =
    user?.permissions?.canAssignClients ??
    (user?.role === "SUPER_ADMIN" || user?.role === "ADMIN")
  const canExportClients =
    user?.role === "SUPER_ADMIN" || user?.role === "ADMIN" || user?.role === "OPS_MANAGER"

  function updateRouteState(next: {
    search?: string | null
    selectedClientId?: string | null
  }) {
    const params = new URLSearchParams(searchParams)

    if (next.search !== undefined) {
      if (next.search) {
        params.set("search", next.search)
      } else {
        params.delete("search")
      }
    }

    if (next.selectedClientId !== undefined) {
      if (next.selectedClientId) {
        params.set("selected_client_id", next.selectedClientId)
      } else {
        params.delete("selected_client_id")
      }
    }

    setSearchParams(params)
  }

  const queryParams = useMemo(
    () => ({
      ...(search ? { search } : {}),
      ...(kycFilter ? { kyc_status: kycFilter } : {}),
      ...(overdueCategoryFilter ? { overdue_category: overdueCategoryFilter } : {}),
      ...(regionFilter ? { region: regionFilter } : {}),
      ...(pipelineFilter ? { pipeline_status: pipelineFilter } : {}),
      ...(assignedFilter ? { is_assigned: assignedFilter } : {}),
      ...(loanTypeFilter ? { loan_type: loanTypeFilter } : {}),
    }),
    [search, kycFilter, overdueCategoryFilter, regionFilter, pipelineFilter, assignedFilter, loanTypeFilter],
  )

  const clientsQuery = useQuery({
    queryKey: ["clients", queryParams],
    queryFn: () => listClients(queryParams),
  })

  const agentsQuery = useQuery({
    queryKey: ["users", "agents"],
    queryFn: () => listUsers({ role: "AGENT", per_page: "100" }),
    enabled: canAssignClients,
  })

  const createMutation = useMutation({
    mutationFn: (payload: ClientFormState) => createClient(payload),
    onSuccess: async () => {
      setForm(DEFAULT_FORM)
      await queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
  })

  const bulkAssignMutation = useMutation({
    mutationFn: (agentId: string) =>
      bulkAssignClients({
        clientIds: selectedClientIds,
        agentId,
      }),
    onSuccess: async () => {
      setSelectedClientIds([])
      setBulkAgentId("")
      await queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
  })

  const exportMutation = useMutation({
    mutationFn: (format: "csv" | "pdf") =>
      generateReport({
        templateKey: "CLIENT_PORTFOLIO_SNAPSHOT",
        format,
        delivery: "download",
        filters: search ? { search } : {},
      }),
  })

  function toggleClientSelection(clientId: string) {
    setSelectedClientIds((current) =>
      current.includes(clientId)
        ? current.filter((id) => id !== clientId)
        : [...current, clientId],
    )
  }

  function openDetail(clientId: string) {
    setSelectedClientId(clientId)
    updateRouteState({ selectedClientId: clientId })
  }

  const clientRows = clientsQuery.data?.data ?? []
  const summary = clientsQuery.data?.meta

  const totalClients = summary?.totalItems ?? 0
  const kycApprovedCount = clientRows.filter((c: any) => c.kycStatus === "APPROVED").length
  const overdueCount = clientRows.filter((c: any) => c.overdueCategory != null).length
  const unassignedCount = clientRows.filter((c: any) => !c.assignedAgent).length
  const totalOutstanding = clientRows.reduce(
    (sum: number, c: any) => sum + (c.outstandingBalance ?? 0),
    0,
  )

  const hasActiveFilters = Boolean(
    search ||
      kycFilter ||
      overdueCategoryFilter ||
      regionFilter ||
      pipelineFilter ||
      assignedFilter ||
      loanTypeFilter,
  )

  function clearAllFilters() {
    setSearch("")
    setKycFilter("")
    setOverdueCategoryFilter("")
    setRegionFilter("")
    setPipelineFilter("")
    setAssignedFilter("")
    setLoanTypeFilter("")
    updateRouteState({ search: null })
  }

  useEffect(() => {
    const routeSearch = searchParams.get("search") ?? ""
    if (routeSearch !== search) {
      setSearch(routeSearch)
    }

    const routeSelectedClientId = searchParams.get("selected_client_id") ?? null
    if (routeSelectedClientId !== selectedClientId) {
      setSelectedClientId(routeSelectedClientId)
    }
  }, [searchParams, search, selectedClientId])

  return (
    <div className="space-y-6">
      <BusinessPageHeader
        eyebrow={
          user?.role === "OPS_MANAGER"
            ? "Operations Manager \u2014 Read-only monitoring"
            : user?.role === "AGENT"
              ? "Agent Portfolio"
              : "Portfolio Management"
        }
        title="Client Portfolio"
        subtitle="Manage client onboarding, KYC review status, loan assignments, and portfolio monitoring across all active accounts."
        meta={
          <>
            <StatusPill tone="info">{totalClients} total clients</StatusPill>
            {unassignedCount > 0 ? (
              <StatusPill tone="warning">{unassignedCount} unassigned on page</StatusPill>
            ) : null}
          </>
        }
        actions={
          <>
            {canCreateClients ? (
              <Button onClick={() => setCreateModalOpen(true)}>New Client</Button>
            ) : null}
            {canExportClients ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => exportMutation.mutate("csv")}
                  disabled={exportMutation.isPending}
                >
                  Export CSV
                </Button>
                <Button
                  onClick={() => exportMutation.mutate("pdf")}
                  disabled={exportMutation.isPending}
                >
                  Export PDF
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <MetricGrid columns="xl:grid-cols-5">
        <StatCard
          label="Total Clients"
          value={totalClients}
          helper={`Page ${summary?.page ?? 1} of ${summary?.totalPages ?? 1}`}
          tone="blue"
        />
        <StatCard
          label="KYC Approved"
          value={kycApprovedCount}
          helper="On current page"
          tone="emerald"
        />
        <StatCard
          label="Outstanding Balance"
          value={formatCurrency(totalOutstanding)}
          helper="Sum of visible rows"
        />
        <StatCard
          label="Overdue Clients"
          value={overdueCount}
          helper="With overdue category assigned"
          tone={overdueCount > 0 ? "amber" : "slate"}
        />
        <StatCard
          label="Unassigned"
          value={unassignedCount}
          helper="No agent assigned"
          tone={unassignedCount > 0 ? "red" : "slate"}
        />
      </MetricGrid>

      <FilterToolbar>
        <FilterField label="Search" widthClassName="min-w-[220px] flex-1">
          <Input
            placeholder="Name, phone number, or loan ID"
            value={search}
            onChange={(event) => {
              const nextSearch = event.target.value
              setSearch(nextSearch)
              updateRouteState({ search: nextSearch || null })
            }}
          />
        </FilterField>
        <FilterField label="KYC Status">
          <Select value={kycFilter} onChange={(e) => setKycFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="APPROVED">Approved</option>
            <option value="ACTION_REQUIRED">Action Required</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
          </Select>
        </FilterField>
        <FilterField label="Overdue Category">
          <Select
            value={overdueCategoryFilter}
            onChange={(e) => setOverdueCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            <option value="S1">S-1</option>
            <option value="CATEGORY_A">Category A</option>
            <option value="CATEGORY_B">Category B</option>
            <option value="CATEGORY_C">Category C</option>
          </Select>
        </FilterField>
        <FilterField label="Pipeline">
          <Select value={pipelineFilter} onChange={(e) => setPipelineFilter(e.target.value)}>
            <option value="">All pipelines</option>
            <option value="NEW">New</option>
            <option value="OLD">Old</option>
            <option value="PROMISE_TO_PAY">Promise To Pay</option>
            <option value="BROKEN_PROMISE">Broken Promise</option>
            <option value="DONE">Done</option>
          </Select>
        </FilterField>
        <FilterField label="Assignment">
          <Select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}>
            <option value="">All</option>
            <option value="true">Assigned</option>
            <option value="false">Unassigned</option>
          </Select>
        </FilterField>
        <FilterField label="Loan Type">
          <Select value={loanTypeFilter} onChange={(e) => setLoanTypeFilter(e.target.value)}>
            <option value="">All loans</option>
            <option value="STANDARD">Standard</option>
            <option value="MICRO">Micro</option>
          </Select>
        </FilterField>
        <FilterField label="Region">
          <Input
            placeholder="Region"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
          />
        </FilterField>
        {hasActiveFilters ? (
          <div className="ml-auto self-end">
            <Button variant="ghost" onClick={clearAllFilters}>
              Clear Filters
            </Button>
          </div>
        ) : null}
      </FilterToolbar>

      {canAssignClients && selectedClientIds.length > 0 && agentsQuery.data?.data?.length ? (
        <FilterToolbar compact>
          <span className="text-sm font-semibold text-slate-700">
            {selectedClientIds.length} client{selectedClientIds.length > 1 ? "s" : ""} selected
          </span>
          <FilterField label="Assign To Agent" widthClassName="min-w-[200px]">
            <Select value={bulkAgentId} onChange={(e) => setBulkAgentId(e.target.value)}>
              <option value="">Select agent</option>
              {agentsQuery.data.data.map((agent: any) => (
                <option key={agent.userId} value={agent.userId}>
                  {agent.fullName}
                </option>
              ))}
            </Select>
          </FilterField>
          <Button
            size="sm"
            onClick={() => bulkAssignMutation.mutate(bulkAgentId)}
            disabled={bulkAssignMutation.isPending || bulkAgentId.length === 0}
          >
            Assign Selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedClientIds([])}>
            Clear Selection
          </Button>
        </FilterToolbar>
      ) : null}

      <ContentCard
        title="Client List"
        subtitle={`${totalClients} client${totalClients !== 1 ? "s" : ""} matching current filters`}
      >
        {clientsQuery.isLoading ? <TableSkeleton rows={8} cols={7} /> : null}

        {clientsQuery.isError ? (
          <div className="rounded-r border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            {(clientsQuery.error as Error).message}
          </div>
        ) : null}

        {clientRows.length === 0 && !clientsQuery.isLoading ? (
          <EmptyState
            description="Try a different filter or seed the local environment with demo client data."
            title="No clients matched"
          />
        ) : null}

        {clientRows.length > 0 ? (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                {canAssignClients ? <Th className="w-10" /> : null}
                <Th>Client</Th>
                <Th>Phone</Th>
                <Th>Loan</Th>
                <Th>KYC</Th>
                <Th>Outstanding</Th>
                <Th>Category</Th>
                <Th>Agent</Th>
              </TableHead>
              <TableBody>
                {clientRows.map((client: any) => (
                  <TableRow key={client.clientId} onClick={() => openDetail(client.clientId)}>
                    {canAssignClients ? (
                      <Td>
                        <div onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedClientIds.includes(client.clientId)}
                            onChange={() => toggleClientSelection(client.clientId)}
                          />
                        </div>
                      </Td>
                    ) : null}
                    <Td>
                      <strong>{client.fullName}</strong>
                      <div className="text-xs text-slate-500">{client.clientId}</div>
                    </Td>
                    <Td>{client.phoneNumber}</Td>
                    <Td>{client.loanId ?? "-"}</Td>
                    <Td>
                      <StatusPill tone={kycTone(client.kycStatus)}>
                        {formatEnumLabel(client.kycStatus)}
                      </StatusPill>
                    </Td>
                    <Td>
                      {client.outstandingBalance != null
                        ? formatCurrency(client.outstandingBalance, client.currency)
                        : "-"}
                    </Td>
                    <Td>
                      {client.overdueCategory ? (
                        <StatusPill tone={overdueCategoryTone(client.overdueCategory)}>
                          {formatEnumLabel(client.overdueCategory)}
                        </StatusPill>
                      ) : (
                        "-"
                      )}
                    </Td>
                    <Td>{client.assignedAgent ?? "-"}</Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </div>
        ) : null}
      </ContentCard>

      {canCreateClients ? (
        <Modal
          open={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false)
            setForm(DEFAULT_FORM)
          }}
          title="Create Client"
          description="Add a new client to the portfolio. KYC review will begin after creation."
        >
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault()
              createMutation.mutate(form, {
                onSuccess: () => setCreateModalOpen(false),
              })
            }}
          >
            <FormField label="Full Name">
              <Input
                onChange={(event) =>
                  setForm((current) => ({ ...current, fullName: event.target.value }))
                }
                required
                value={form.fullName}
              />
            </FormField>
            <FormField label="Phone Number">
              <Input
                onChange={(event) =>
                  setForm((current) => ({ ...current, phoneNumber: event.target.value }))
                }
                required
                value={form.phoneNumber}
              />
            </FormField>
            <FormField label="Region" className="sm:col-span-2">
              <Input
                onChange={(event) =>
                  setForm((current) => ({ ...current, region: event.target.value }))
                }
                value={form.region}
              />
            </FormField>
            <div className="sm:col-span-2 flex items-center gap-3">
              <Button type="submit" disabled={createMutation.isPending}>
                Create Client
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setCreateModalOpen(false)
                  setForm(DEFAULT_FORM)
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
          {createMutation.isError ? (
            <div className="mt-3 rounded-r border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
              {(createMutation.error as Error).message}
            </div>
          ) : null}
        </Modal>
      ) : null}

      <ClientDetailDrawer
        clientId={selectedClientId}
        open={Boolean(selectedClientId)}
        onClose={() => {
          setSelectedClientId(null)
          updateRouteState({ selectedClientId: null })
        }}
      />
    </div>
  )
}
